import { SessionProtocol as SessionProtocolV1 } from './v1/SessionProtocol'
import { SessionProtocolStrategy } from './VPSessionProtocolStrategy'

export class VPServerConnection {
  private static instance: VPServerConnection | undefined

  private readonly sessionProtocolStrategy: SessionProtocolStrategy
  private readonly server =
    process.env.NEXT_PUBLIC_FEATURE_VIRTUALPADS_CONNECTION_SERVER

  private readonly maxConnectionRetries = Number(
    process.env.NEXT_PUBLIC_FEATURE_VIRTUALPADS_CONNECTION_WSS_MAX_RETRIES
  )

  private readonly connectionRetryDelay = Number(
    process.env.NEXT_PUBLIC_FEATURE_VIRTUALPADS_CONNECTION_DELAY
  )

  private socket: WebSocket | null = null
  private connectionRetries = 0
  private connectionPromise: Promise<void> | null = null
  private connectionReject: ((reason?: unknown) => void) | null = null
  private retryTimeout: number | null = null
  private intentionallyClosed = false
  private onUnexpectedClose: (() => void) | null = null

  /**
   * Initializes the VPServerConnection
   */
  private constructor () {
    this.sessionProtocolStrategy = new SessionProtocolStrategy(
      new SessionProtocolV1()
    )
  }

  /**
   * Returns the VPServerConnection instance, creating it if necessary
   * @returns The VPServerConnection instance
   */
  public static getInstance (): VPServerConnection {
    if (VPServerConnection.instance === undefined) {
      VPServerConnection.instance = new VPServerConnection()
    }

    return VPServerConnection.instance
  }

  /**
   * Connects to the virtual pad server
   *
   * If a connection attempt is already in progress, returns the same promise
   * instead of creating another WebSocket
   */
  public connect (): Promise<void> {
  if (this.connectionPromise !== null) {
    return this.connectionPromise
  }

  if (this.socket?.readyState === WebSocket.OPEN) {
    return this.sessionProtocolStrategy.waitUntilCreated()
  }

  this.intentionallyClosed = false
  this.connectionRetries = 0

  this.connectionPromise = new Promise<void>((resolve, reject) => {
    this.connectionReject = reject
    this.openSocket(resolve, reject)
  })

  return this.connectionPromise
}

  /**
   * Opens a WebSocket connection to the virtual pad server
   */
  private openSocket (
    resolve: () => void,
    reject: (reason?: unknown) => void
  ): void {
    if (this.intentionallyClosed) {
      this.rejectConnection(
        reject,
        new Error('VIRTUAL PAD :: CONNECT :: Connection cancelled')
      )
      return
    }

    if (this.server === undefined || this.server.trim() === '') {
      this.rejectConnection(
        reject,
        new Error('VIRTUAL PAD :: CONNECT :: Unknown server')
      )
      return
    }

    const url = `wss://${this.server}`

    let socket: WebSocket

    try {
      socket = new WebSocket(url)
    } catch (error) {
      this.handleConnectionFailure(resolve, reject, error)
      return
    }

    this.socket = socket
    socket.binaryType = 'arraybuffer'

    let connectionOpened = false
    let connectionFailureHandled = false

    socket.onopen = async () => {
      if (socket !== this.socket) return

      connectionOpened = true
      this.connectionRetries = 0

      // console.warn(`${new Date().toISOString()} Virtual pad WebSocket opened`)

      try {
        this.sessionProtocolStrategy.create()
        await this.sessionProtocolStrategy.waitUntilCreated()
        this.resolveConnection(resolve)
      } catch (error) {
        connectionFailureHandled = true

        this.sessionProtocolStrategy.reset()
        this.socket = null
        socket.close()

        this.rejectConnection(reject, error)
      }
    }

    socket.onmessage = (e: MessageEvent) => {
      if (socket !== this.socket) return
      this.sessionProtocolStrategy.handleMessage(e)
    }

    socket.onerror = (e: Event) => {
      if (socket !== this.socket) return

      console.error('Virtual pad WebSocket error:', e)

      /*
       * The close event is responsible for deciding whether the failed
       * connection attempt should be retried.
       */
      if (
        socket.readyState === WebSocket.CONNECTING ||
        socket.readyState === WebSocket.OPEN
      ) {
        socket.close()
      }
    }

    socket.onclose = (e: CloseEvent) => {
      if (socket !== this.socket) return

      this.socket = null

      console.warn(
        `${new Date().toISOString()} Virtual pad WebSocket closed`,
        {
          code: e.code,
          reason: e.reason,
          wasClean: e.wasClean
        }
      )

      const wasIntentionallyClosed = this.intentionallyClosed

      /*
      * The signaling protocol no longer has an active connection,
      * regardless of whether the socket closed intentionally.
      */
      this.sessionProtocolStrategy.reset()

      if (wasIntentionallyClosed || connectionFailureHandled) return

      /*
      * The connection was successfully established and then lost.
      * Notify the session coordinator, but do not reconnect
      * automatically.
      */
      if (connectionOpened) {
        this.onUnexpectedClose?.()
        return
      }

      /*
      * The socket never opened, so the closure belongs to the
      * current explicit connection attempt.
      */
      connectionFailureHandled = true

      this.handleConnectionFailure(
        resolve,
        reject,
        new Error(
          `VIRTUAL PAD :: CONNECT :: Connection closed before opening (${e.code})`
        )
      )
    }
  }

  /**
   * Handles a failed connection attempt and schedules another attempt
   * when the retry limit has not been reached.
   */
  private handleConnectionFailure (
    resolve: () => void,
    reject: (reason?: unknown) => void,
    error: unknown
  ): void {
    if (this.intentionallyClosed) {
      this.rejectConnection(
        reject,
        new Error('VIRTUAL PAD :: CONNECT :: Connection cancelled')
      )
      return
    }

    if (this.connectionRetries >= this.getMaxConnectionRetries()) {
      console.error(
        'VIRTUAL PAD :: CONNECT :: Maximum number of connection retries reached'
      )

      this.rejectConnection(reject, error)
      return
    }

    this.connectionRetries++

    const delay = this.getConnectionRetryDelay()

    this.clearRetryTimeout()

    this.retryTimeout = window.setTimeout(() => {
      this.retryTimeout = null
      this.openSocket(resolve, reject)
    }, delay * 1000)
  }

  /**
   * Returns the configured maximum number of retries.
   */
  private getMaxConnectionRetries (): number {
    if (!Number.isFinite(this.maxConnectionRetries)) {
      return 0
    }

    return Math.max(0, this.maxConnectionRetries)
  }

  /**
   * Returns the configured delay between connection retries in seconds.
   */
  private getConnectionRetryDelay (): number {
    if (!Number.isFinite(this.connectionRetryDelay)) {
      return 0
    }

    return Math.max(0, this.connectionRetryDelay)
  }

  /**
   * Resolves the current connection attempt.
   */
  private resolveConnection (resolve: () => void): void {
    this.clearRetryTimeout()
    this.connectionPromise = null
    this.connectionReject = null

    resolve()
  }

  /**
   * Rejects the current connection attempt.
   */
  private rejectConnection (
    reject: (reason?: unknown) => void,
    error: unknown
  ): void {
    this.clearRetryTimeout()
    this.connectionPromise = null
    this.connectionReject = null

    reject(error)
  }

  /**
   * Clears the pending connection retry.
   */
  private clearRetryTimeout (): void {
    if (this.retryTimeout === null) {
      return
    }

    window.clearTimeout(this.retryTimeout)
    this.retryTimeout = null
  }

  /**
   * Sends a message to the server via WebSocket.
   * @param message Message to send.
   */
  public sendMessage (message: object): void {
    if (this.socket?.readyState !== WebSocket.OPEN) {
      throw new Error(
        'VIRTUAL PAD :: SEND MESSAGE :: The socket is not open'
      )
    }

    this.socket.send(JSON.stringify(message))
  }

  /**
   * Closes the connection to the server.
   */
  public close (): void {
    this.intentionallyClosed = true
    this.connectionRetries = 0

    this.clearRetryTimeout()

    if (this.connectionReject !== null) {
      const reject = this.connectionReject

      this.connectionPromise = null
      this.connectionReject = null

      reject(
        new Error('VIRTUAL PAD :: CONNECT :: Connection cancelled')
      )
    }

    const socket = this.socket

    if (socket === null) {
      this.sessionProtocolStrategy.reset()
      return
    }

    if (socket.readyState === WebSocket.OPEN) {
      try {
        this.sessionProtocolStrategy.close()
      } catch (error) {
        console.error(
          'VIRTUAL PAD :: CLOSE :: Could not notify the server',
          error
        )
      }

      // The protocol will be reset from the socket's close event.
      socket.close(1000, 'Browser closed')
      return
    }

    if (socket.readyState === WebSocket.CONNECTING) {
      // The protocol will be reset from the socket's close event.
      socket.close()
      return
    }

    this.socket = null
    this.sessionProtocolStrategy.reset()
  }

  /**
   * Get the current server URL
   * @returns Current server URL
   */
  public getServer () {
    return this.server
  }

  /**
   * Get the current session protocol
   * @returns Current session protocol
   */
  public getSessionProtocol () {
    return this.sessionProtocolStrategy
  }

  /**
   * Registers the callback executed when an established WebSocket
   * connection closes unexpectedly.
   *
   * @param callback Callback executed after an unexpected disconnection
   */
  public setOnUnexpectedClose(callback: (() => void) | null): void {
    this.onUnexpectedClose = callback
  }
}